import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios';
import {
  LS_USER_ACCOUNT_ID,
  LS_USER_API_KEY,
  LS_USER_API_SIG,
  LS_IQ11_TOKEN,
  LS_USER_JWT,
  LS_MERCHANT_TOKEN,
  LS_USER_LOGIN_USER_NAME,
} from '../constants';

import { getMfaHash } from '../utils/utils';

const ss = window.sessionStorage;
const ll = window.localStorage;

type GetProps = {
  url: string;
  source?: CancelTokenSource;
  config?: AxiosRequestConfig;
  bearer?: string;
  GPVersion?: string;
};

type PostProps<T> = {
  url: string;
  config: AxiosRequestConfig;
  source?: CancelTokenSource;
  overrideAuth?: {
    AccountID: string;
    Key: string;
    Signature: string;
  };
  data?: T;
  bearer?: string;
  GPVersion?: string;
  body?: string;
};

type PutProps<T> = {
  url: string;
  config: AxiosRequestConfig;
  source?: CancelTokenSource;
  data?: T;
  bearer?: string;
  GPVersion?: string;
  body?: string;
};

export type Http = {
  request: <T = unknown, R = T>(config: AxiosRequestConfig) => Promise<AxiosResponse<R>>;
  get: <T = unknown, R = T>(getProps: GetProps) => Promise<AxiosResponse<R>>;
  post: <T = unknown, R = T>(postProps: PostProps<T>) => Promise<AxiosResponse<R>>;
  put: <T = unknown, R = T>(putProps: PutProps<T>) => Promise<AxiosResponse<R>>;
};

export type UserTokens = {
  AccountID: string | null;
  Key: string | null;
  Signature: string | null;
  IframeKey: string | null;
};

export const createHttp = (baseURL: string): Http => {
  const instance = initAxiosInstance(baseURL);

  const request = <T = unknown, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> =>
    instance.request(injectTokens(config));

  const get = <T = unknown, R = AxiosResponse<T>>({
    url,
    config = {},
    source,
    bearer,
    GPVersion,
  }: GetProps): Promise<R> => instance.get<T, R>(url, injectTokens(config, source, bearer, GPVersion));

  const post = <T = unknown, R = AxiosResponse<T>>({
    url,
    config = {},
    source,
    overrideAuth,
    body,
    bearer,
    GPVersion,
  }: PostProps<T>): Promise<R> => {
    const newConfig = injectTokens(config, source, bearer, GPVersion, overrideAuth);
    return instance.post<T, R>(url, body, newConfig);
  };

  const put = <T = unknown, R = AxiosResponse<T>>({
    url,
    config = {},
    source,
    body,
    bearer,
    GPVersion,
  }: PostProps<T>): Promise<R> => {
    const newConfig = injectTokens(config, source, bearer, GPVersion);
    return instance.put<T, R>(url, body, newConfig);
  };

  return { request, get, post, put };
};

function initAxiosInstance(url: string): AxiosInstance {
  const instance = axios.create({
    baseURL: url,
    headers: getHeaders(),
    withCredentials: false,
  });

  return instance;
}

function getUserTokensFromStorage(params: { [key: string]: string }): UserTokens {
  return {
    AccountID: ss.getItem(LS_USER_ACCOUNT_ID) || ll.getItem(LS_USER_ACCOUNT_ID),
    Key: ss.getItem(LS_USER_API_KEY) || ll.getItem(LS_USER_API_KEY),
    Signature: ss.getItem(LS_USER_API_SIG) || ll.getItem(LS_USER_API_SIG),
    IframeKey: ss.getItem(LS_IQ11_TOKEN) || params?.IframeKey,
  };
}

function getHeaders(): Readonly<Record<string, string | boolean>> {
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json; charset=utf-8',
  };
}

function injectTokens(
  config: AxiosRequestConfig,
  source?: CancelTokenSource,
  bearer?: string,
  GPVersion?: string,
  overrideAuth?: {
    AccountID: string;
    Key: string;
    Signature: string;
  },
): AxiosRequestConfig {
  const cancelToken = source?.token;
  let userTokens = {};
  let authorizationHeader = {};
  let GPVersionHeader = {};
  let merchantHeader = {};

  if (bearer) {
    authorizationHeader = { Authorization: `Bearer ${bearer}` };
    if (GPVersion) {
      GPVersionHeader = { 'X-GP-Version': GPVersion, 'Content-Type': 'application/json' };
    }
  } else if (ss.getItem(LS_USER_JWT) !== null) {
    authorizationHeader = { Authorization: `Bearer ${ss.getItem(LS_USER_JWT) || ''}` };
    userTokens = getUserTokensFromStorage(config.params as { [key: string]: string });
  } else if (ll.getItem(LS_USER_JWT) !== null) {
    authorizationHeader = { Authorization: `Bearer ${ll.getItem(LS_USER_JWT) || ''}` };
  } else if (ll.getItem(LS_MERCHANT_TOKEN) !== null) {
    merchantHeader = {
      MerchantUserToken: ll.getItem(LS_MERCHANT_TOKEN),
    };
  } else {
    userTokens = getUserTokensFromStorage(config.params as { [key: string]: string });
  }

  const headers = {
    ...(config.headers as { [key: string]: string }),
    ...authorizationHeader,
    ...GPVersionHeader,
    ...merchantHeader,
  };

  const params = {
    ...(config.params as { [key: string]: string }),
    ...userTokens,
    cancelToken,
    ...overrideAuth,
    MfaHash: getMfaHash(localStorage.getItem(LS_USER_LOGIN_USER_NAME)),
  };

  if (!GPVersion) {
    return { ...config, params, headers };
  }

  return { ...config, headers };
}
